import {
  ArchiveRestore,
  Download,
  Eye,
  type LucideIcon,
  Printer,
  SlidersHorizontal,
  Sparkles,
  SquarePen,
  SquarePlus,
  Users,
  X,
} from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { Button } from './Button'
import { Menu } from './Menu'
import './page-action.css'
import { Tooltip } from './Tooltip'

/**
 * The top bar's action slot.
 *
 * A section's primary action ("New message") belongs beside the page title and
 * its tabs, not inside a table's toolbar — it acts on the section, not on the
 * list, and it has to stay put when the section switches between views that
 * aren't tables at all. A screen declares one with `<PageAction>`; the bar
 * renders whatever the current screen declared, and nothing when it declared
 * none.
 */
/**
 * What KIND of thing an action is. The header offers one entry point; the types
 * order what is behind it, and each action wears its type's glyph beside its
 * name unless it names a glyph for what the act is.
 *
 * Typed rather than ranked because ranking is a question with a different answer
 * on every screen — it never transfers, so nothing a person learns on one page
 * is worth anything on the next. The vocabulary here is the same everywhere.
 */
export type PageActionType =
  | 'view'
  | 'assistant'
  | 'edit'
  | 'create'
  | 'restore'
  | 'settings'
  | 'share'
  | 'print'
  | 'download'

/**
 * The order they appear in, and the reason: what the action does to the
 * collection. Changes before outputs; within outputs, the printed sheet before
 * the file. A new type is placed by asking the same question of it, never by
 * appending here.
 *
 * `share` changes WHO the thing is for, which is a change rather than an
 * output — a link is not a file you take away, it is other people reaching what
 * stays here. So it sits with the changes, after creating, and both outputs
 * follow it.
 *
 * `settings` changes what the thing IS — its own properties, as against what it
 * contains. That places it after creating, which adds to the thing, and before
 * sharing, which decides who else reaches it: the narrowing runs from the
 * contents outward to the audience.
 *
 * `edit` is the innermost point of that same narrowing, so it comes first among
 * the changes: it alters what is ALREADY there, where creating adds to it. The
 * two are close enough to want one word and are not one type — a person with a
 * write share may rewrite every passage of a book and add no part to it, and a
 * type that covered both would offer them the act they cannot perform.
 *
 * `restore` adds to the collection as `create` does, and differs only in where
 * what it adds comes from — so it sits directly after it. It is what a
 * collection with a recoverable delete owes: a record that has left is
 * reachable only from here, and a window nobody can look through is one the
 * purge closes on the last day. It acts on the CONTENT rather than on the reader,
 * which is what keeps it out of `view`.
 *
 * `view` leads, and it is the only one that does not belong to that sequence at
 * all: every other type acts on the CONTENT, and this one acts on the READER. How
 * somebody has set the text they are reading changes nothing anybody else will
 * ever see and produces nothing — so it is the one entry point with no
 * consequence to weigh, and the one reached for most. It goes first for both
 * reasons.
 */
const TYPE_ORDER: PageActionType[] = [
  'view',
  'assistant',
  'edit',
  'create',
  'restore',
  'settings',
  'share',
  'print',
  'download',
]

/* A bare plus is a hairline beside three drawn shapes and reads as a stray mark
   between them; the squared one carries the same weight as its neighbors. */
const TYPE_ICON = {
  view: Eye,
  assistant: Sparkles,
  edit: SquarePen,
  create: SquarePlus,
  restore: ArchiveRestore,
  settings: SlidersHorizontal,
  share: Users,
  print: Printer,
  download: Download,
} as const

export type PageActionItem = {
  id: string
  type: PageActionType
  /** Names the action in full — it is read in a menu, or as a tooltip. */
  label: string
  /**
   * What the act is, as a glyph — Add person, Record a payment — where the
   * type's own glyph would say only that something is created. The type still
   * decides where the action sits; this only decides what it wears.
   */
  icon?: LucideIcon
  /**
   * How many things behind it are waiting on the person to ANSWER. Zero draws
   * nothing.
   *
   * A count, and the test for one is that the person can bring it to zero by
   * attending to what it names — an offer accepted or declined, an unread line
   * read. Something that merely sits behind the door for ever fails that test
   * and takes `holding` instead: a number nobody can spend is a demand the
   * screen cannot make good on, and it reads as one beside the marks that can.
   */
  waiting?: number
  /**
   * Whether there is anything behind it at all.
   *
   * The same job as `waiting` minus the arithmetic — it stops a person opening
   * an empty drawer — and it is deliberately not a number, because the number
   * would say nothing they could act on. A deleted book is the case: how many
   * are in there is a fact about storage, not a queue.
   *
   * Drawn as a quiet dot, and outranked by `waiting` wherever an action has
   * both. A count already implies the door is not empty, so the two together
   * would be one mark saying what the other just said.
   */
  holding?: boolean
  /**
   * Whether what this action opened is on screen NOW.
   *
   * A STATE, never a second type. The header still shows types — the glyph a
   * control draws while it is offering to open something is its type's, on
   * every screen. This says the offer has been taken: the thing is showing, and
   * what the control is for until it goes is putting it away.
   *
   * Only for something that opens IN PLACE and leaves the page working — a
   * reader's display row. A Sheet, a Modal or the band covers or replaces the
   * page and carries its own way out, so none of them declares this.
   */
  open?: boolean
  onSelect: () => void
}

/**
 * How a collection makes a new record — the one place the pair is described.
 *
 * Held as a description rather than as rendered buttons so the shell draws them:
 * a screen says what its two actions do, and where they sit, which of them is the
 * confident one and how they answer a phone stay decisions of the shell.
 */
export type PageCreate = {
  /** Names what the form makes — "Add rule", "New message". */
  addLabel: string
  onAdd: () => void
  /** Opens the assistant for this collection. Only where one can help. */
  onAssist?: () => void
  /** What is added, as a glyph — a person, a rule — rather than a bare plus. */
  addIcon?: LucideIcon
}

const PageActionContext = createContext<{
  action: ReactNode
  setAction: (action: ReactNode) => void
  /** Every typed action the current screen has declared. */
  items: PageActionItem[]
  register: (item: PageActionItem) => void
  unregister: (id: string) => void
  /**
   * The handheld row's slot for a list's own tools.
   *
   * A phone gets ONE row of chrome, holding everything the page offers — the
   * search, the filters, the view, the count and the actions. The tools belong to
   * the screen and the row belongs to the shell, so they meet by the screen
   * portalling into this element rather than by the shell copying nodes out of a
   * context: the search field keeps its own state, and typing in it does not have
   * to travel through the shell to redraw.
   */
  toolSlot: HTMLElement | null
  setToolSlot: (el: HTMLElement | null) => void
  /**
   * The same meeting place, at the row's trailing end.
   *
   * A screen's tools LEAD the row — the search is the one control reached for
   * without looking. What opens something standing at the page's trailing edge
   * belongs at that edge here too, so shut and open are reached from one side
   * and opening is a change of width rather than of place.
   *
   * Two slots rather than one with an order applied, because the hairline
   * between cells is drawn from DOM order: reordering visually would put the
   * divider on the outside of the first cell and leave the last without one.
   */
  endSlot: HTMLElement | null
  setEndSlot: (el: HTMLElement | null) => void
  /**
   * The top bar's slot for who else is on this page.
   *
   * Presence is not an action and not navigation — it is a fact about the page,
   * and the one thing in the bar that is about other PEOPLE. It sits beside the
   * account for that reason: who I am working as, and who else is here.
   *
   * A slot rather than something the shell reads out of context, for the reason
   * the two below are: the roster keeps its own polling and its own state, and
   * redrawing the shell every time somebody arrives would redraw every page.
   */
  presenceSlot: HTMLElement | null
  setPresenceSlot: (el: HTMLElement | null) => void
  /**
   * The slot above an open document, which every note about it fills — who is
   * reading along, its details, a notice about how it prints. See `DocSlot`.
   */
  docSlot: HTMLElement | null
  setDocSlot: (el: HTMLElement | null) => void
  title: ReactNode
  setTitle: (title: ReactNode) => void
  info: ReactNode
  setInfo: (info: ReactNode) => void
  back: (() => void) | null
  setBack: (back: (() => void) | null) => void
  studioDoc: DocGround | null
  setDoc: (studioDoc: DocGround | null) => void
} | null>(null)

/**
 * What an open document lies on: the table, or a paper that takes the whole
 * screen.
 *
 * A document read in long runs — a book — is the paper itself, edge to edge,
 * because an edge drawn around running text is read as the boundary of the
 * thing and makes everything outside it look like another app. A document that
 * is a sheet of a stated size — a page, a deck — lies on the table, so its edge
 * is its size.
 */
export type DocGround = 'paper' | 'table'

export function PageActionProvider({ children }: { children: ReactNode }) {
  const [action, setAction] = useState<ReactNode>(null)
  const [items, setItems] = useState<PageActionItem[]>([])

  /*
   * Registered by id rather than by position: a screen's actions mount in
   * whatever order its JSX happens to sit in, and the shell sorts them by type
   * regardless — so nothing here depends on when a declarer arrives.
   */
  const register = useCallback((item: PageActionItem) => {
    setItems((prev) => [...prev.filter((i) => i.id !== item.id), item])
  }, [])
  const unregister = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])
  const [toolSlot, setToolSlot] = useState<HTMLElement | null>(null)
  const [endSlot, setEndSlot] = useState<HTMLElement | null>(null)
  const [presenceSlot, setPresenceSlot] = useState<HTMLElement | null>(null)
  const [docSlot, setDocSlot] = useState<HTMLElement | null>(null)
  const [title, setTitle] = useState<ReactNode>(null)
  const [info, setInfo] = useState<ReactNode>(null)
  /*
   * A record page's way back to the list it came from.
   *
   * Held by the shell rather than drawn by the screen, because it shares a slot
   * with the nav control and only one of them is ever right: a page about one
   * record is somewhere you arrived from a list, so the useful move is back to
   * that list, and the global nav is a tap further away through it. Giving the
   * slot to one control instead of two also makes it twice the target.
   */
  const [back, setBackState] = useState<(() => void) | null>(null)
  /*
   * Wrapped for two reasons, and both are load-bearing.
   *
   * The updater form, because `useState` CALLS a function it is handed — passing
   * the callback directly would invoke the navigation instead of storing it.
   *
   * And `useCallback`, because `PageBack`'s effect depends on this: recreated
   * every render, the effect re-ran every render, and its cleanup cleared the
   * value it had just set. The back control never appeared and the header went on
   * showing the nav button.
   */
  const setBack = useCallback(
    (fn: (() => void) | null) => setBackState(() => fn),
    [],
  )
  const [studioDoc, setDoc] = useState<DocGround | null>(null)
  return (
    <PageActionContext.Provider
      value={{
        action,
        setAction,
        items,
        register,
        unregister,
        toolSlot,
        setToolSlot,
        endSlot,
        setEndSlot,
        presenceSlot,
        setPresenceSlot,
        docSlot,
        setDocSlot,
        title,
        setTitle,
        info,
        setInfo,
        back,
        setBack,
        studioDoc,
        setDoc,
      }}
    >
      {children}
    </PageActionContext.Provider>
  )
}

/** Read the declared action — for the top bar itself. */
export function usePageAction(): ReactNode {
  return useContext(PageActionContext)?.action ?? null
}

/** The handheld row's tool slot — for a list header portalling into it. */
export function usePageToolSlot(): HTMLElement | null {
  return useContext(PageActionContext)?.toolSlot ?? null
}

/** Register the handheld row's tool slot — for the shell that draws the row. */
export function useSetPageToolSlot(): (el: HTMLElement | null) => void {
  const ctx = useContext(PageActionContext)
  return ctx?.setToolSlot ?? (() => {})
}

/** The handheld row's trailing slot — for whatever opens at that edge. */
export function usePageEndSlot(): HTMLElement | null {
  return useContext(PageActionContext)?.endSlot ?? null
}

/** Register the handheld row's trailing slot — for the shell that draws it. */
export function useSetPageEndSlot(): (el: HTMLElement | null) => void {
  const ctx = useContext(PageActionContext)
  return ctx?.setEndSlot ?? (() => {})
}

/** The top bar's slot for who else is here — null where there is no bar. */
export function usePagePresenceSlot(): HTMLElement | null {
  return useContext(PageActionContext)?.presenceSlot ?? null
}

/** Register it — for the shell that draws the bar. */
export function useSetPagePresenceSlot(): (el: HTMLElement | null) => void {
  const ctx = useContext(PageActionContext)
  return ctx?.setPresenceSlot ?? (() => {})
}

/** The slot above an open document — null where no document owns the screen. */
export function useDocSlot(): HTMLElement | null {
  return useContext(PageActionContext)?.docSlot ?? null
}

/** Register it — for the shell that draws it. */
export function useSetDocSlot(): (el: HTMLElement | null) => void {
  const ctx = useContext(PageActionContext)
  return ctx?.setDocSlot ?? (() => {})
}

/** Read every typed action this screen declared — for the top bar itself. */
export function usePageActionItems(): PageActionItem[] {
  return useContext(PageActionContext)?.items ?? []
}

/** Read the declared title — for the top bar itself. */
export function usePageTitle(): ReactNode {
  return useContext(PageActionContext)?.title ?? null
}

/** Read the declared page blurb — for the top bar itself. */
export function usePageInfo(): ReactNode {
  return useContext(PageActionContext)?.info ?? null
}

/** Read the declared way back — for the top bar itself. */
export function usePageBack(): (() => void) | null {
  return useContext(PageActionContext)?.back ?? null
}

/**
 * Declare where this page goes back to — the list a record was opened from.
 *
 * The shell draws it in the header's leading slot, in place of the nav control:
 * on a page about one record, back to the list is the move, and the global nav
 * is one tap further through it. Two controls in one slot would each be half the
 * target.
 */
export function PageBack({ to }: { to: () => void }) {
  const ctx = useContext(PageActionContext)
  const setBack = ctx?.setBack
  /*
   * Held in a ref, and registered once.
   *
   * A screen writes `to={() => navigate(...)}`, which is a new function on every
   * render — so an effect depending on its identity re-ran every render, and its
   * cleanup cleared the value the previous run had just set. Setting state then
   * re-rendered, producing another new function, and round again: the control
   * never appeared and the header went on showing the nav button.
   *
   * The ref keeps the newest callback without the effect having to watch it.
   */
  const latest = useRef(to)
  latest.current = to
  useEffect(() => {
    if (!setBack) return
    setBack(() => latest.current())
    return () => setBack(null)
  }, [setBack])
  return null
}

/** Whether the screen is an open document, and what it lies on — for the shell. */
export function usePageDoc(): DocGround | null {
  return useContext(PageActionContext)?.studioDoc ?? null
}

/**
 * Declare this screen an open document.
 *
 * An open document owns the screen: the shell draws no sidebar and no brand. In
 * Studio it stands between two bars, its contents and open documents on the
 * left and its tools on the right; elsewhere one slim band holds the way back,
 * the document's name, who else is here, its tools and the account.
 */
export function PageDoc({ ground }: { ground: DocGround }) {
  const setDoc = useContext(PageActionContext)?.setDoc
  useEffect(() => {
    if (!setDoc) return
    setDoc(ground)
    return () => setDoc(null)
  }, [setDoc, ground])
  return null
}

/**
 * Declare this screen's blurb — what the page is for, in a sentence or two.
 *
 * It opens from a ⓘ beside the page title, so it costs the page no height. A
 * note about one group of fields belongs to that FieldSection's description.
 */
export function PageInfo({ children }: { children: ReactNode }) {
  const ctx = useContext(PageActionContext)
  const setInfo = ctx?.setInfo
  useEffect(() => {
    if (!setInfo) return
    setInfo(children)
    return () => setInfo(null)
  }, [setInfo, children])
  return null
}

/**
 * Declare this screen's title, for a page whose name is data rather than a
 * route — one scheduled message, one member. It takes the top bar's title
 * position, standing in for the section's tabs, because a record's own name is
 * where you are; the tab strip belongs to the list you came from.
 */
export function PageTitle({ children }: { children: ReactNode }) {
  const ctx = useContext(PageActionContext)
  const setTitle = ctx?.setTitle
  useEffect(() => {
    if (!setTitle) return
    setTitle(children)
    return () => setTitle(null)
  }, [setTitle, children])
  return null
}

/**
 * Declare this screen's primary action. Renders nothing where it sits; the top
 * bar picks it up. Clearing on unmount is what keeps a stale button from
 * outliving the screen that owned it.
 */
export function PageAction({ children }: { children: ReactNode }) {
  const ctx = useContext(PageActionContext)
  const setAction = ctx?.setAction
  useEffect(() => {
    if (!setAction) return
    setAction(children)
    return () => setAction(null)
  }, [setAction, children])
  return null
}

/**
 * Declare how this collection makes a new record.
 *
 * Creating one acts on the **section**, not on the list in front of you — it has
 * to survive the list becoming a board or a set of cards, and it is the screen's
 * one confident mark, which belongs beside the page's name rather than at the end
 * of a row of filters. So it is a page action, and the toolbar holds tools.
 *
 * The callbacks ride in refs and the registration runs once, for the reason
 * `PageBack` documents: a screen writes `onAdd={() => …}`, which is a new function
 * every render, and an effect watching its identity clears on re-render whatever
 * it had just set.
 */
export function PageCreate({ addLabel, addIcon, onAdd, onAssist }: PageCreate) {
  return (
    <>
      {onAssist ? (
        <PageActionFor
          type="assistant"
          label="Set up with AI"
          onSelect={onAssist}
        />
      ) : null}
      <PageActionFor
        type="create"
        label={addLabel}
        icon={addIcon}
        onSelect={onAdd}
      />
    </>
  )
}

/**
 * Declare one typed action on this screen.
 *
 * The screen says what it can do and what KIND of thing that is; the shell
 * decides whether it appears as an icon or as an item inside one. A screen that
 * grew a second print artifact changes nothing about the header.
 *
 * The callback rides in a ref and registration runs once, for the reason
 * `PageBack` documents: a screen writes `onSelect={() => …}`, a new function
 * every render, and an effect watching its identity clears on re-render
 * whatever it had just set.
 */
export function PageActionFor({
  type,
  label,
  icon,
  onSelect,
  waiting,
  holding,
  open,
}: {
  type: PageActionType
  label: string
  /** What the act is, where the type's glyph would say less. */
  icon?: LucideIcon
  onSelect: () => void
  /** Whether what this opened is showing. See `PageActionItem.open`. */
  open?: boolean
  /**
   * How many things behind this action are waiting on the person.
   *
   * An entry point somebody has no reason to open is one they never open, so a
   * screen holding something addressed to them says so on the control that
   * reveals it. It is a COUNT of things to answer, not a decoration: zero draws
   * nothing, because a mark that is always there stops being a mark.
   *
   * Only for something the person can settle. What merely sits there takes
   * `holding` — see `PageActionItem`, where the pair is described.
   */
  waiting?: number
  /** Whether there is anything behind it. See `PageActionItem.holding`. */
  holding?: boolean
}) {
  const ctx = useContext(PageActionContext)
  const register = ctx?.register
  const unregister = ctx?.unregister
  const id = useId()
  const latest = useRef(onSelect)
  latest.current = onSelect
  useEffect(() => {
    if (!register || !unregister) return
    register({
      id,
      type,
      label,
      icon,
      waiting,
      holding,
      open,
      onSelect: () => latest.current(),
    })
    return () => unregister(id)
  }, [register, unregister, id, type, label, icon, waiting, holding, open])
  return null
}

/**
 * A page's actions in their type order, each wearing its glyph — the one
 * sequence every surface listing them reads, the menu and a phone's Actions
 * sheet alike.
 */
export function orderedPageActions(items: PageActionItem[]) {
  return TYPE_ORDER.flatMap((type) =>
    items
      .filter((i) => i.type === type)
      .map((i) => ({ ...i, icon: i.icon ?? TYPE_ICON[type] })),
  )
}

/**
 * The header's action slot: one control per TYPE the screen declared.
 *
 * One action of a type is that action, as an icon with its name in a tooltip.
 * Several are a dropdown under the type's own icon. Which it is follows from
 * counting, so a screen never decides and adding an action never moves anything.
 *
 * A type with nothing in it renders nothing — no held slot. The SEQUENCE is what
 * is learnable (print is always after create, before download); a reserved gap
 * would buy exact position at the price of a header full of holes.
 */
export function PageActionGroups({
  items,
  compact = false,
}: {
  items: PageActionItem[]
  /**
   * Drawn as one glyph rather than a word, for a floating rail with the width
   * of an icon. Still ONE entry point, and a menu even for a single act, so
   * the rail's cell means the same thing on every document.
   */
  compact?: boolean
}) {
  /*
   * While one action is holding something open, it is the ONLY thing the header
   * offers.
   *
   * The others are not useful yet: what is on screen is the thing that was
   * opened, and reaching for a different one would mean putting this away
   * first. Showing them anyway asks somebody to work that out from a row of
   * controls, when the header can simply say the one thing there is to do.
   * They return the moment it closes, which is one press.
   */
  const held = items.find((i) => i.open)
  if (held && compact) {
    const shut = `Close ${held.label.toLowerCase()}`
    return (
      <Tooltip label={shut}>
        <Button variant="quiet" icon aria-label={shut} onClick={held.onSelect}>
          <X size={16} aria-hidden />
        </Button>
      </Tooltip>
    )
  }
  if (held) {
    const shut = `Close ${held.label.toLowerCase()}`
    return (
      <Tooltip label={shut}>
        <Button
          variant="quiet"
          aria-label={shut}
          onClick={held.onSelect}
          className="pageaction__shut"
        >
          <X className="btn__icon" aria-hidden />
          <span className="pageaction__word">{shut}</span>
        </Button>
      </Tooltip>
    )
  }
  /*
   * ONE entry point. The bar carries the nav control, the page's identity, one
   * way into what the page can do, and the account — and a page is more than
   * what can be done to it, so the slot has to be one control however many
   * actions a screen declares. A book says who else is reading it in the room
   * that leaves.
   *
   * The types still order the list, so what a screen offers reads the same way
   * everywhere and the sequence is still the learnable thing. Each action keeps
   * its type's glyph, or one naming the act, beside its name: in a menu the label is what identifies
   * it, and the icon says what KIND of act it is at a glance.
   */
  const listed = orderedPageActions(items).map((i) => ({
    /*
     * The count travels WITH the action it belongs to. Summed onto the
     * trigger it named nothing — a screen with four deleted books and no
     * offers showed "4" beside a menu holding both, and a reader had to
     * open each to learn which of the two it meant.
     */
    label: i.waiting ? `${i.label} — ${i.waiting}` : i.label,
    /* Presence travels with its action for the same reason the count does,
       and stays a mark rather than joining the label: what it knows is
       that the drawer is not empty, and a number said in words would be
       the figure this action has no business claiming. */
    holding: !i.waiting && i.holding,
    icon: i.icon,
    onSelect: i.onSelect,
  }))
  if (listed.length === 0) return null

  /*
   * Whether anything at all is waiting, as a mark rather than a number.
   *
   * A total across unrelated things is not a quantity anybody can act on, and
   * the actual counts now sit on the items. What the trigger owes is that there
   * is something in there addressed to this person, so it says that much and no
   * more.
   */
  const waiting = items.some((i) => (i.waiting ?? 0) > 0)

  /*
   * One action is drawn as itself, named, rather than wrapped in a menu.
   *
   * The rule is that the bar carries ONE way into what a page can do — which a
   * lone action already is. Putting it behind a trigger called "Actions" costs
   * a tap and replaces the word that said what the thing does with a word that
   * says nothing, and a menu offering a single item is a button to a button.
   * The moment a screen declares a second, the menu is back and the sequence
   * decides the order, so nothing here is tuned to a count.
   */
  if (compact) {
    return (
      <span className="pageaction__typed">
        <Tooltip label="Actions">
          <Menu
            ariaLabel={waiting ? 'Actions, something waiting' : 'Actions'}
            align="end"
            items={listed}
          />
        </Tooltip>
        {waiting && <span className="pageaction__waiting" aria-hidden />}
      </span>
    )
  }

  if (listed.length === 1) {
    const only = listed[0]
    const Icon = only.icon
    return (
      <span className="pageaction__typed">
        <Tooltip label={only.label}>
          {/* The word is not drawn in the bar, so the name is carried here:
              a hidden word names nothing to a screen reader. */}
          <Button
            variant="quiet"
            aria-label={only.label}
            onClick={only.onSelect}
          >
            <Icon className="btn__icon" aria-hidden />
            <span className="pageaction__word">{only.label}</span>
          </Button>
        </Tooltip>
        {waiting && <span className="pageaction__waiting" aria-hidden />}
      </span>
    )
  }

  /* Presence does NOT reach the trigger, and that is the whole distinction:
     the mark here says something inside is addressed to this person, and a
     drawer that merely has things in it addresses nobody. It says so on its
     own item, where a reader can see which action it belongs to. */

  return (
    <span className="pageaction__typed">
      {/* NAMED rather than a bare glyph. It is the one way into everything a
          screen can do, so it has to be findable without being opened — and an
          unlabeled `⋯` beside a stack of faces is two marks that read alike.
          Still quiet: the bar carries no confident mark. */}
      <Menu
        label="Actions"
        /* The mark is drawn for the eye and hidden from the reader that cannot
           see it, so the name is where it is actually said. */
        ariaLabel={waiting ? 'Actions, something waiting' : 'Actions'}
        align="end"
        items={listed}
      />
      {waiting && <span className="pageaction__waiting" aria-hidden />}
    </span>
  )
}
